import { useState, useEffect } from 'react';
import { reviewsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { getTodayDateString } from '../../lib/utils';

const RATING_FIELDS = [
  ['product_knowledge', 'Product Knowledge'],
  ['customer_vendor_management', 'Customer / Vendor Management'],
  ['target_achievement', 'Target Achievement'],
  ['crm_tradeiq_usage', 'CRM / TradeIQ Usage'],
  ['rma_management', 'RMA Management', 'Handles RMAs and claims with customers and vendors on time: paperwork, follow-up and recovery.'],
  ['team_management', 'Team Management', 'Guides and follows up their team: targets, CRM/Trade IQ discipline, coaching and escalations.'],
];
const SENIOR_ONLY_FIELDS = new Set(['team_management']);

export function CreateReviewModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [reviewDate, setReviewDate] = useState(getTodayDateString());
  const [ratings, setRatings] = useState({});
  const [targetPct, setTargetPct] = useState('');
  const [comments, setComments] = useState('');
  const [actionItems, setActionItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    reviewsAPI.employees().then((r) => setEmployees((r.data || []).filter((e) => e.id !== user?.id))).catch(() => {});
    reviewsAPI.trainers().then((r) => setTrainers(r.data || [])).catch(() => {});
    setEmployeeId(''); setReviewDate(getTodayDateString()); setRatings({});
    setTargetPct(''); setComments(''); setActionItems([]);
  }, [open]);

  const addActionItem = () => setActionItems([...actionItems, { description: '', due_date: '', trainer_user_id: '' }]);
  const updateActionItem = (i, field, value) => {
    const next = [...actionItems];
    next[i] = { ...next[i], [field]: value };
    setActionItems(next);
  };
  const removeActionItem = (i) => setActionItems(actionItems.filter((_, idx) => idx !== i));

  const selectedEmployee = employees.find((e) => e.id === employeeId);
  const isSenior = selectedEmployee?.review_level === 'SENIOR';
  const criteriaFields = RATING_FIELDS.filter(([key]) => isSenior || !SENIOR_ONLY_FIELDS.has(key));

  const handleSubmit = async () => {
    if (!employeeId) { toast.error('Select an employee'); return; }
    setSaving(true);
    try {
      const criteriaRatings = Object.fromEntries(criteriaFields.map(([key]) => [key, ratings[key]]).filter(([, v]) => v !== undefined));
      const payload = {
        employee_user_id: employeeId, review_date: reviewDate, ...criteriaRatings,
        target_achievement_pct: targetPct ? parseFloat(targetPct) : null,
        comments,
        action_items: actionItems.filter((a) => a.description.trim()).map((a) => ({
          description: a.description, due_date: a.due_date || null, trainer_user_id: a.trainer_user_id || null,
        })),
      };
      const res = await reviewsAPI.create(payload);
      toast.success('Review created as draft');
      onCreated(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-review-modal">
        <DialogHeader><DialogTitle>New Performance Review</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="create-review-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}{e.crm_role_name ? ` (${e.crm_role_name})` : ''}{e.review_level === 'SENIOR' ? ' — Sr Executive and above' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSenior && (
              <Badge variant="outline" className="mt-1" data-testid="create-review-senior-badge">Sr Executive and above</Badge>
            )}
          </div>
          <div>
            <Label>Review Date</Label>
            <Input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} data-testid="create-review-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {criteriaFields.map(([key, label, help]) => (
              <div key={key}>
                <Label>{label}</Label>
                {help && <p className="text-xs text-muted-foreground mb-1">{help}</p>}
                <Select value={ratings[key] ? String(ratings[key]) : ''} onValueChange={(v) => setRatings({ ...ratings, [key]: parseInt(v) })}>
                  <SelectTrigger data-testid={`create-review-rating-${key}`}><SelectValue placeholder="Rate 1-5" /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div>
            <Label>Target Achievement % (from Zoho CRM)</Label>
            <Input type="number" step="0.1" value={targetPct} onChange={(e) => setTargetPct(e.target.value)} data-testid="create-review-target-pct-input" />
          </div>
          <div>
            <Label>Comments</Label>
            <Textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="create-review-comments-input" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Action Items</Label>
              <Button type="button" size="sm" variant="outline" onClick={addActionItem} data-testid="create-review-add-action-item-btn">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2 mt-2">
              {actionItems.map((item, i) => (
                <div key={i} className="flex gap-2 items-start border rounded-md p-2" data-testid={`create-review-action-item-${i}`}>
                  <Input placeholder="Description" value={item.description}
                    onChange={(e) => updateActionItem(i, 'description', e.target.value)} className="flex-1" />
                  <Input type="date" value={item.due_date} onChange={(e) => updateActionItem(i, 'due_date', e.target.value)} className="w-36" />
                  <Select value={item.trainer_user_id} onValueChange={(v) => updateActionItem(i, 'trainer_user_id', v)}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="Trainer (optional)" /></SelectTrigger>
                    <SelectContent>
                      {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeActionItem(i)} data-testid={`create-review-remove-action-item-${i}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="create-review-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
